import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { buyersApi, type BuyerLeadListParams } from "@/api/buyers";
import { formatCurrency } from "@/design-system/lib/format";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { buyerPhaseLadder } from "../../buyerPhases";
import { AttentionTile } from "../../indexTiles/AttentionTile";
import { IndexTileDeck, Measure, MeasureTile } from "../../indexTiles/MeasureTile";
import { StageDistributionTile } from "../../indexTiles/StageDistributionTile";
import { VolumeTile } from "../../indexTiles/VolumeTile";
import { ratePct, type StageSegment, type StageTone } from "../../indexTiles/tileBits";
import { QueryErrorState } from "../../queryStates";
import { listHref } from "../reportLinks";

/** The rung → colour map, declared per phase (the list page's, copied — the
 *  tab may not import from the page file). */
const BUYER_PHASE_TONE: Record<string, StageTone> = {
  intake: "early",
  contact: "working",
  qualified: "advanced",
  submitting: "nearly",
  won: "won",
};

function pctLabel(numerator?: number, denominator?: number): string {
  const pct = ratePct(numerator ?? 0, denominator ?? 0);
  return pct === null ? "—" : `${pct}%`;
}

/**
 * The four buyer tiles the Buyers list used to carry, over the SAME stats read
 * the list makes — same numbers, same query key — narrowed to one team when
 * the page has one. A click on a rung or a pill opens the list filtered the
 * way the tile used to filter it.
 */
export function BuyersReportTab({ teamId }: { teamId: string | undefined }) {
  const navigate = useNavigate();
  const params = useMemo<BuyerLeadListParams>(
    () => ({ page: 1, buyerTeamId: teamId }),
    [teamId],
  );
  const stats = useQuery({
    queryKey: buyerKeys.stats(params),
    queryFn: () => buyersApi.stats(params),
  });

  // D10 — the window is the server's; until the payload states it the pill
  // carries no number rather than a guess.
  const noContactDays = stats.data?.noContactDays;

  const stageSegments = useMemo<StageSegment[]>(() => {
    const byStatus = stats.data?.byStatus ?? {};
    return buyerPhaseLadder.phases.map((p) => ({
      key: p.key,
      label: p.label,
      count: p.statuses.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0),
      tone: BUYER_PHASE_TONE[p.key] ?? "working",
    }));
  }, [stats.data]);

  if (stats.isError) {
    return (
      <QueryErrorState
        what="buyer figures"
        onRetry={() => void stats.refetch()}
        retrying={stats.isFetching}
      />
    );
  }

  const open = (param: "phase" | "card", key: string | null) =>
    navigate(listHref("/buyers", { [param]: key, team: teamId, scope: "all" }));

  return (
    <IndexTileDeck>
      <StageDistributionTile
        title="Funnel stage"
        segments={stageSegments}
        onSelect={(key) => open("phase", key)}
        loading={stats.isLoading}
      />

      <AttentionTile
        items={[
          {
            key: "overdue",
            label: "Overdue follow-up",
            count: stats.data?.overdueFollowUp ?? 0,
            severity: "overdue",
          },
          {
            key: "no-contact",
            label: noContactDays == null ? "No contact" : `No contact in ${noContactDays} days`,
            count: stats.data?.noContactRecently ?? 0,
          },
          {
            key: "qualified-no-submission",
            label: "Qualified, no submission",
            count: stats.data?.qualifiedNoSubmission ?? 0,
          },
          {
            key: "unassigned",
            label: "Unassigned",
            count: stats.data?.unassigned ?? 0,
          },
        ]}
        onSelect={(key) => open("card", key)}
        loading={stats.isLoading}
      />

      <VolumeTile
        title="Buyers captured"
        points={stats.data?.createdByMonth ?? []}
        loading={stats.isLoading}
      />

      <MeasureTile title="Capacity and conversion">
        <Measure
          label="Qualified capacity"
          value={stats.data ? formatCurrency(stats.data.qualifiedCapacity ?? 0) : "—"}
          detail="Sum of what qualified buyers can spend"
        />
        {/* Both rates divide by OWNED buyers — one denominator, and the
            numerator travels with it (the list page's own rule). */}
        <Measure
          label="Qualified"
          value={pctLabel(stats.data?.assignedQualified, stats.data?.assignedTotal)}
          detail={`${stats.data?.assignedQualified ?? 0} of ${stats.data?.assignedTotal ?? 0} owned qualified`}
        />
        <Measure
          label="Converted"
          value={pctLabel(stats.data?.assignedConverted, stats.data?.assignedTotal)}
          detail={`${stats.data?.assignedConverted ?? 0} of ${stats.data?.assignedTotal ?? 0} owned converted`}
        />
      </MeasureTile>
    </IndexTileDeck>
  );
}
